// Realtime connection to a game session (used by both the TV and the phones).
// Handles reconnection, clock synchronisation and exposes the personalised state.
import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { EVT, type AckResponse, type ClientAction, type ClockSyncResponse, type GameEvent, type HostCommand, type StateMessage } from '@shared/protocol';

/** Shared with the connection test page so it measures exactly what the games use. */
export const SOCKET_TRANSPORTS: { transports: string[]; tryAllTransports: boolean } = { transports: ['websocket', 'polling'], tryAllTransports: true };

export type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'closed' | 'kicked' | 'invalid';

export interface GameSocket<V = any> {
  status: ConnectionStatus;
  state: StateMessage<V> | null;
  /** performance.now() when the current state arrived — used for latency-free reaction timing. */
  receivedAt: number;
  rtt: number | null;
  serverNow(): number;
  action(type: string, payload?: unknown): Promise<AckResponse>;
  command(cmd: HostCommand): Promise<AckResponse>;
  onEvent(fn: (e: GameEvent) => void): () => void;
}

export function useGameSocket<V = any>(token: string | null): GameSocket<V> {
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [state, setState] = useState<{ msg: StateMessage<V> | null; at: number }>({ msg: null, at: 0 });
  const [rtt, setRtt] = useState<number | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const offsetRef = useRef(0);
  const listeners = useRef(new Set<(e: GameEvent) => void>());
  const pauseRef = useRef<{ pausedAt: number | null; total: number }>({ pausedAt: null, total: 0 });

  useEffect(() => {
    if (!token) return;
    // WebSocket first; hosts whose proxy refuses WebSocket (some shared hosting) fall back to HTTP long-polling.
    const socket = io({ auth: { token }, ...SOCKET_TRANSPORTS, reconnectionDelay: 500, reconnectionDelayMax: 3000 });
    socketRef.current = socket;
    let terminal = false;

    const sync = async () => {
      // Pick the sample with the lowest round trip: its midpoint best estimates server time.
      let best: { rtt: number; offset: number } | null = null;
      for (let i = 0; i < 5 && socket.connected; i++) {
        const sent = Date.now();
        const res = await socket.timeout(2000).emitWithAck(EVT.clockSync, sent).catch(() => null) as ClockSyncResponse | null;
        if (!res) continue;
        const now = Date.now();
        const sample = { rtt: now - sent, offset: res.serverTime - (sent + now) / 2 };
        if (!best || sample.rtt < best.rtt) best = sample;
      }
      if (best) {
        offsetRef.current = best.offset;
        setRtt(best.rtt);
      }
    };

    socket.on('connect', () => {
      setStatus('connected');
      void sync();
    });
    socket.on('disconnect', (reason) => {
      if (terminal) return;
      setStatus('reconnecting');
      // Socket.IO only retries by itself after network drops; if the server hung up we must ask again.
      if (reason === 'io server disconnect') socket.connect();
    });
    socket.on('connect_error', (err) => {
      if (err.message === 'invalid_token' || err.message === 'session_not_found' || err.message === 'player_not_found') {
        terminal = true;
        setStatus('invalid');
        socket.disconnect();
      } else setStatus('reconnecting');
    });
    socket.on(EVT.state, (msg: StateMessage<V>) => {
      // Fall back to the message timestamp until a proper sync has happened.
      if (offsetRef.current === 0) offsetRef.current = msg.serverTime - Date.now();
      pauseRef.current = msg.session.pause ?? { pausedAt: null, total: 0 };
      setState({ msg, at: performance.now() });
    });
    socket.on(EVT.event, (e: GameEvent) => listeners.current.forEach((fn) => fn(e)));
    socket.on(EVT.kicked, () => {
      terminal = true;
      setStatus('kicked');
    });
    socket.on(EVT.closed, () => {
      terminal = true;
      setStatus('closed');
    });
    const resync = setInterval(() => void sync(), 30_000);

    // Phones freeze background pages and drop half-open connections. Coming back to the page, getting
    // network again, or two missed heartbeats all force a fresh connection (the server then resends the
    // whole state), so a screen can never stay frozen or blank.
    const wake = () => {
      if (terminal || document.visibilityState === 'hidden') return;
      if (!socket.connected) socket.connect();
      else void sync();
    };
    let missed = 0;
    const heartbeat = setInterval(async () => {
      if (terminal) return;
      if (!socket.connected) {
        socket.connect();
        return;
      }
      const res = await socket.timeout(3000).emitWithAck(EVT.clockSync, Date.now()).catch(() => null);
      missed = res ? 0 : missed + 1;
      if (missed >= 2) {
        missed = 0;
        socket.disconnect();
        socket.connect();
      }
    }, 5000);
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('online', wake);
    window.addEventListener('pageshow', wake);

    return () => {
      clearInterval(resync);
      clearInterval(heartbeat);
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('online', wake);
      window.removeEventListener('pageshow', wake);
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token]);

  const emit = useCallback((event: string, payload: unknown): Promise<AckResponse> => {
    const socket = socketRef.current;
    if (!socket?.connected) return Promise.resolve({ ok: false, error: 'Bağlantı yok, yeniden bağlanılıyor…' });
    return socket
      .timeout(5000)
      .emitWithAck(event, payload)
      .catch(() => ({ ok: false, error: 'Sunucu yanıt vermedi' }));
  }, []);

  const action = useCallback((type: string, payload?: unknown) => emit(EVT.playerAction, { type, payload } satisfies ClientAction), [emit]);
  const command = useCallback((cmd: HostCommand) => emit(EVT.hostCommand, cmd), [emit]);
  /** Game clock (matches engine timestamps): stops while the game is paused. */
  const serverNow = useCallback(() => {
    const { pausedAt, total } = pauseRef.current;
    return (pausedAt ?? Date.now() + offsetRef.current) - total;
  }, []);
  const onEvent = useCallback((fn: (e: GameEvent) => void) => {
    listeners.current.add(fn);
    return () => void listeners.current.delete(fn);
  }, []);

  return { status, state: state.msg, receivedAt: state.at, rtt, serverNow, action, command, onEvent };
}
